<template>
  <div :class="['flyout-calendar', { return: isReturn }]">
    <modal-header-white
      v-if="isMobile"
      class="calendar-header"
      :device-type="isMobile ? 'mobile' : 'desktop'"
      :label="modalTitle"
      @closed="onClosed"
    />
    <div
      v-if="!isMobile"
      ref="calendarWhitePrevMonth"
      tabindex="0"
      :class="['control prev-month', { disabled: backDisabled }]"
      @click="minusOneMonth"
      @keyup.enter="minusOneMonth"
      @keyup.esc="onEscClosed"
    />
    <div
      v-if="!isMobile"
      tabindex="0"
      :class="['control next-month', { disabled: nextDisabled }]"
      @click="plusOneMonth"
      @keyup.enter="plusOneMonth"
      @keyup.esc="onEscClosed"
    />
    <div class="months-wrapper">
      <div
        v-for="(monthData, monthIndex) in getMonths()"
        :key="monthIndex"
        class="month"
      >
        <div class="heading">{{ monthData.monthAndYear }}</div>
        <date-table
          :selection-mode="selectionMode"
          :date="date"
          :year="monthData.year"
          :month="monthData.month"
          :disabled-date="disabledDate"
          :min-date="minDate"
          :max-date="maxDate"
          :range-state="rangeState"
          :first-day-of-week="firstDayOfWeek"
          :is-return="isReturn"
          :direct-flights="
            isReturn && returnSelected ? directFlightsRoundTrip : directFlights
          "
          @changerange="handleChangeRange"
          @pick="handleRangePick"
          @close="onClosed"
          @escClose="onEscClosed"
        />
      </div>
    </div>
    <div
      v-if="showFooter"
      class="bottom-block"
    >
      <div
        v-if="!loadingStatus"
        class="sign"
      >
        <span class="block" />
        {{ directFlightsText }}
      </div>
      <div
        v-else
        class="loading"
      >
        <div class="loading-icon" />
        <p>{{ directFlightsText }}</p>
      </div>
    </div>
  </div>
</template>

<script>
  import DateUtils from 'utils/DateUtils'
  import moment from 'moment'
  import ModalHeaderWhite from 'components/Modal/ModalHeaderWhite'
  import DateTable from 'components/Dates/DateTableWhite'

  /**
   * Exported events
   */
  const events = {
    CLOSED: 'closed',
    ESC_CLOSED: 'escClosed',
    MIN_DATE_PICKED: 'minDatePicked',
    MAX_DATE_PICKED: 'maxDatePicked'
  }

  export default {
    components: {
      ModalHeaderWhite,
      DateTable
    },
    props: {
      defaultMinDate: {
        type: [Date, String, Object],
        default: ''
      },
      defaultMaxDate: {
        type: [Date, String, Object],
        default: ''
      },
      tripType: {
        type: String,
        default: ''
      },
      isReturn: {
        type: Boolean,
        default: false
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      modalTitle: {
        type: String,
        required: true
      },
      origin: {
        type: Object,
        required: false,
        default: () => null
      },
      destin: {
        type: Object,
        required: false,
        default: () => null
      },
      directFlights: {
        type: Object,
        required: false,
        default: () => {}
      },
      directFlightsRoundTrip: {
        type: Object,
        default: () => {}
      },
      directFlightsText: {
        type: String,
        default: ''
      },
      autoOpenDate: {
        type: [Date, Boolean],
        default: false
      },
      loadingStatus: {
        type: Boolean,
        required: true
      },
      returnSelected: {
        type: Boolean,
        required: false,
        default: false
      },
      showFooter: {
        type: Boolean,
        required: false,
        default: true
      },
      monthsToPrint: {
        type: Number,
        required: false,
        default: 1
      },
      returnDateLimit: {
        type: Number,
        required: false,
        default: 0
      },
      departureDateLimit: {
        type: Number,
        required: false,
        default: 0
      }
    },
    data() {
      return {
        date: this.defaultMinDate,
        minDate: DateUtils.resetTime(
          this.defaultMinDate !== '' ? this.defaultMinDate : new Date()
        ),
        maxDate: this.defaultMaxDate,
        rangeState: {
          endDate: null,
          selecting: this.defaultMaxDate !== '',
          row: null,
          column: null
        },
        futureLimit: moment(new Date().setHours(0, 0, 0, 0)).add('359', 'days'),
        firstDayOfWeek: 1,
        selectionMode: ['return', 'multi'].includes(this.tripType) ? 'range' : 'day',
        disabledDate: this.disabledBeforeNow,
        monthsStartDate: this.getMonthStartDate(),
        backDisabled: true,
        nextDisabled: false,
        futureLimitYear: 359
      }
    },

    computed: {
      selectionModes() {
        return ['return', 'multi'].includes(this.tripType) ? 'range' : 'day'
      }
    },
    watch: {
      tripType(val) {
        if (val === 'oneway') {
          this.maxDate = ''
        }
      },
      isReturn() {
        this.disabledDate = this.disabledBeforeNow
        this.monthsStartDate = this.getMonthStartDate()
        this.backDisabled = this.getBackDisabled()
      },
      defaultMinDate(val) {
        this.minDate = val
      },
      defaultMaxDate(val) {
        this.maxDate = val
      }
    },
    mounted() {
      // for mobile phones: scroll to selected departure month
      if (this.isMobile) {
        const min = this.minDate.getMonth()
        const start = this.monthsStartDate.getMonth()
        if (
          min === start
          && this.minDate.getFullYear() === this.monthsStartDate.getFullYear()
        ) {
          return
        }
        let index = min - start
        if (min < start) {
          index = 12 - start + min
        } else if (min === start) {
          index = 12
        }
        let height = 0
        const months = document.getElementsByClassName('month')
        for (let i = 0; i < index; i++) {
          height +=
            (months[parseInt(i)].clientHeight ||
              months[parseInt(i)].offsetHeight ||
              months[parseInt(i)].scrollHeight) + 1
        }
        document.getElementsByClassName('months-wrapper')[0].scrollTop = height
      }

      this.backDisabled = this.getBackDisabled()

      if (['return', 'multi'].includes(this.tripType)) {
        this.futureLimit = this.setFutureLimit(this.returnDateLimit)
      }
      if (this.departureDateLimit > 0 && ['departure'].includes(this.tripType)) {
        this.futureLimit = this.setFutureLimit(this.departureDateLimit, true)
      }
    },
    methods: {
      setFutureLimit(customLimit, useTodayDate = false) {
        let limit = customLimit > 0 ? customLimit : this.futureLimitYear
        let minDate = new Date().setHours(0, 0, 0, 0)

        if (!useTodayDate) {
          minDate = this.minDate ? this.minDate : new Date().setHours(0, 0, 0, 0)
        }
        return moment(minDate).add(limit, 'days')
      },
      getBackDisabled() {
        const dateStartMonth = moment.isMoment(this.monthsStartDate)
          ? this.monthsStartDate.month()
          : this.monthsStartDate.getMonth()
        const minMonth = moment.isMoment(this.minDate)
          ? this.minDate.month()
          : this.minDate.getMonth()

        if (!this.isReturn) {
          return (
            moment().month() === dateStartMonth
            && moment().year() >= moment(this.monthsStartDate).year()
          )
        } else {
          return (
            dateStartMonth <= minMonth
            && this.monthsStartDate.getFullYear() === this.minDate.getFullYear()
          )
        }
      },
      getMonthStartDate() {
        if (this.isMobile) {
          return DateUtils.resetTime(new Date())
        } else if (!this.isReturn) {
          return DateUtils.resetTime(
            this.defaultMinDate !== '' ? this.defaultMinDate : new Date()
          )
        } else {
          let maxDate = new Date()
          if (this.defaultMaxDate !== '' && this.defaultMinDate !== '') {
            const defaultMaxMonth = moment.isMoment(this.defaultMaxDate)
              ? this.defaultMaxDate.month()
              : this.defaultMaxDate.getMonth()
            const defaultMinMonth = moment.isMoment(this.defaultMinDate)
              ? this.defaultMinDate.month()
              : this.defaultMinDate.getMonth()

            maxDate =
              defaultMaxMonth === defaultMinMonth
                ? this.defaultMaxDate
                : new Date(moment(this.defaultMaxDate).add(-1, 'months'))
          } else if (this.defaultMinDate !== '') {
            maxDate = this.autoOpenDate ? this.autoOpenDate : this.defaultMinDate
          }
          return DateUtils.resetTime(maxDate)
        }
      },
      disabledBeforeMin(date) {
        let beforeMin = false
        if (this.minDate) {
          beforeMin =
            DateUtils.resetTime(date).getTime() <
            DateUtils.resetTime(this.minDate).getTime()
        }
        return this.disabledBeforeNow(date) || beforeMin
      },
      disabledBeforeNow(date) {
        const now = new Date()
        date = new Date(date.setHours(0, 0, 0, 0))
        now.setHours(0, 0, 0, 0)
        const beforeNow = date < now
        const afterFutureLimit = this.futureLimit.diff(moment(date)) < 0

        return beforeNow || afterFutureLimit
      },
      onClosed() {
        this.$emit(events.CLOSED)
      },
      onEscClosed() {
        this.$emit(events.ESC_CLOSED)
      },
      handleChangeRange(val) {
        if (!['return', 'multi'].includes(this.tripType)) {
          if (!this.disabledBeforeNow(val.minDate)) {
            this.minDate = val.minDate
            this.maxDate = val.maxDate
            this.rangeState = val.rangeState
          }
        } else {
          if (!this.disabledBeforeMin(val.minDate)) {
            this.minDate = val.minDate
            this.maxDate = val.maxDate
            this.rangeState = val.rangeState
          }
        }
      },

      handleRangePick(val) {
        if (!['return', 'multi'].includes(this.tripType)) {
          if (!this.disabledBeforeNow(val.minDate)) {
            this.minDate = val.minDate
            this.maxDate = ''
            this.futureLimit = this.setFutureLimit(this.returnDateLimit)

            this.date = val.minDate ? val.minDate : val
            this.$emit(events.MIN_DATE_PICKED, this.date, this.monthsStartDate)
            this.$emit(events.MAX_DATE_PICKED, '')
          }
        } else {
          if (!this.isReturn) {
            this.date = val.minDate ? val.minDate : val
            if (!this.disabledBeforeNow(val.minDate)) {
              this.minDate = val.minDate
              this.maxDate = ''
              this.$emit(events.MIN_DATE_PICKED, val.minDate, this.monthsStartDate)
              this.$emit(events.MAX_DATE_PICKED, '')
              this.monthsStartDate = val.minDate
            }
          } else {
            this.date = val.maxDate ? val.maxDate : val
            if (val.maxDate !== null && val.maxDate !== '') {
              if (!this.disabledBeforeMin(val.maxDate)) {
                this.maxDate = val.maxDate
                this.$emit(events.MAX_DATE_PICKED, val.maxDate)
              }
            }
          }
        }
      },

      getMonths() {
        const start = moment(this.monthsStartDate)
        const monthArray = []

        let monthsToPrint = this.monthsToPrint
        if (this.isMobile) {
          monthsToPrint = new Date().getMonth() === this.futureLimit.month() ? 13 : 12
        }

        for (let i = 0; i < monthsToPrint; i++) {
          const date = start.clone().add(i, 'months')
          monthArray.push({
            monthAndYear: date.format('MMMM YYYY'),
            month: date.month(),
            year: date.year()
          })
        }
        return monthArray
      },
      plusOneMonth() {
        if (
          moment().month() === moment(this.monthsStartDate).month()
          && moment().year() < moment(this.monthsStartDate).year()
        ) {
          this.nextDisabled = true
        } else if (!this.nextDisabled) {
          this.monthsStartDate = new Date(moment(this.monthsStartDate).add(1, 'months'))
          this.backDisabled = this.getBackDisabled()
          this.nextDisabled =
            this.futureLimit.month() === this.monthsStartDate.getMonth()
            && moment().year() < moment(this.monthsStartDate).year()
        }
      },
      minusOneMonth() {
        if (moment().isSame(this.monthsStartDate, 'month')) {
          this.backDisabled = true
        } else {
          this.monthsStartDate = new Date(
            moment(this.monthsStartDate).clone().add(-1, 'months')
          )
          this.backDisabled = this.getBackDisabled()
          this.nextDisabled = false
        }
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/keyframes.scss';

  ::v-deep .calendar-header {
    background-color: white;
    border-bottom: 1px solid $grey-border;
    display: block;
    height: 56px;
    padding: 28px 24px;

    .modal-title {
      color: $blue-dark;
      font-size: 14px !important;
      font-weight: 500;
      line-height: 16px;
    }

    .close-icon {
      font-size: em(14);
      right: 25px;
      color: $blue-medium;
    }
  }

  .flyout-calendar {
    display: flex;
    flex-direction: column;

    z-index: 100;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background-color: white;

    @include bp-mf-tablet {
      left: calc(50% - 161px);
      top: 50px;
      height: auto;
      padding-bottom: 5px;
      width: 323px;
      border-radius: 3px;
      position: absolute;
      box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.16);
      justify-content: flex-start;
    }

    .modal-header {
      font-size: $base-font-size;
    }

    .control {
      position: absolute;
      top: 18px;
      width: 24px;
      height: 24px;
      background: white;
      font-size: $base-font-size;
      text-align: center;
      cursor: pointer;
      z-index: 4;

      @include outline-focus;

      &.disabled::before {
        color: $calendar-border;
        cursor: default;
      }

      &::before {
        @include icon-font-thin();
        font-size: em(13);
        line-height: 24px;
        color: $blue-medium;
      }

      &.prev-month {
        z-index: 4;
        left: 16px;

        &:before {
          content: $icon-arrow-right-thin-medium;
          transform: rotate(180deg);
        }
      }

      &.next-month {
        right: 16px;
        z-index: 4;

        &:before {
          content: $icon-arrow-right-thin-medium;
        }
      }
    }

    .months-wrapper {
      padding-top: 60px;
      width: 100%;
      height: auto;
      overflow: auto;
      white-space: nowrap;
      background-color: white;
      margin-bottom: 40px;

      @include bp-mf-tablet {
        padding-top: 60px;
        margin-bottom: 14px;
      }
    }

    .month {
      margin: 0 24px 28px;
      font-size: $base-font-size;

      @include bp-mf-tablet {
        margin-bottom: 0;
      }

      .heading {
        color: $blue-dark;
        font-size: em(16);
        font-weight: 500;
        line-height: 19px;
        margin-bottom: 16px;

        @include bp-mf-tablet {
          position: absolute;
          top: 22px;
          width: 50%;
          left: calc(50% - 80px);
          margin: 0;
          text-align: center;
        }
      }
    }

    .sign {
      vertical-align: center;
      position: relative;
      padding: 0 25px;
      font-size: em(12);
      color: $grey;
      line-height: 48px;
      height: 48px;
      display: flex;

      @include bp-mf-tablet {
        border-top: 1px solid #dbdbdb;
      }

      .block {
        display: inline-block;
        border-radius: 4px;
        margin-top: 12px;
        margin-right: 10px;
        background-color: white;
        width: 24px;
        height: 24px;
        position: relative;

        @include bp-mf-tablet {
          width: 23px;
          height: 23px;
          background-color: #f6f6f6;
        }

        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 2px;
          right: 2px;
          width: 0;
          height: 0;
          border-top: 6px solid $green-dark;
          border-left: 6px solid transparent;
        }
      }
    }

    .bottom-block {
      background-color: #f3f3f3;
      color: $grey;
      font-size: em(12);
      line-height: 14px;

      @include bp-mf-tablet {
        background-color: white;
      }

      .loading {
        p {
          display: inline-block;
          vertical-align: middle;
          margin: 0;
        }
      }

      .loading-icon,
      .loading-icon:after {
        width: 25px;
        height: 25px;
        border-radius: 50%;
      }

      .loading-icon {
        position: relative;
        display: inline-block;
        vertical-align: middle;
        margin-right: 5px;
        border: 3px solid #ebeded;
        transform: translateZ(0);
        animation: spin 1.1s infinite linear;
        text-indent: -9999em;
      }
    }
  }
</style>
